import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Table
} from "react-bootstrap";
import ControleHistogramModal from "../../Components/processo/Charts/ControleHistogram";
import ControleDashboardCard from "../../Components/processo/ControleDashboardCard";
import EstatisticaControleCard from "../../Components/processo/EstatisticaDoControleDashBoard";
import PlanoDashboardCard from "../../Components/processo/PlanoDashboardCard";
import RegistroviewDashboardCard from "../../Components/processo/RegistroviewDashboardCard";
import { fmtNum, hasStatAlert } from "../../Services/processo/dashboardProcessoUtils";


function ProcessControleDashboard({ data = {} }) {
  const planos = data?.planosDashboards || [];

  // Modal do histograma
  const [showHist, setShowHist] = useState(false);

  // seleção de plano/controle
  const planoIds = useMemo(() => planos.map((p) => String(p.planoId)), [planos]);
  const [selectedPlanoId, setSelectedPlanoId] = useState(planoIds[0] || null);

  const planoSelecionado = useMemo(
    () => planos.find((p) => String(p.planoId) === String(selectedPlanoId)) || null,
    [planos, selectedPlanoId]
  );

  const nomePlanoSelecionado =
    planoSelecionado?.nomePlano || `Plano ${selectedPlanoId}`;

  // Estatísticas do plano selecionado (apenas estatística)
  const estatisticasPlano =
    planoSelecionado?.estatisticas ||
    {};

  const estatisticasFrequenciaPlano = planoSelecionado?.analiseFrequencia

  // lista de controles
  const controlesList = planoSelecionado?.controlesDashboard || [];
  const [selectedControleId, setSelectedControleId] = useState(
    controlesList[0]?.controleId || null
  );

  // ao trocar plano, reset controle
  useEffect(() => {
    setSelectedControleId(controlesList[0]?.controleId || null);
  }, [selectedPlanoId, planoSelecionado]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = useMemo(() => {
    return (
      controlesList.find(
        (c) => String(c.controleId) === String(selectedControleId)
      ) || null
    );
  }, [controlesList, selectedControleId]);

  // opções do seletor de plano com dados de desvios
  const planoOptions = planos.map((p) => ({
    value: String(p.planoId),
    label: p.nomePlano
      ? `${p.nomePlano}`
      : `Plano ${p.planoId}`,
    totalRegistrosForaGlobal: p.estatisticas?.totalRegistrosForaGlobal ?? 0,
    itensAlertaEstatistica: p.estatisticas?.itensAlertaEstatistica ?? 0,
  }));
  // lista lateral de controles
  const controleOptions = (controlesList || []).map((cobj) => ({
    value: String(cobj.controleId),
    label: `${cobj.controleId} — ${cobj?.controle?.nomeParametro || "Parâmetro"
      }`,
    nomeParametro: cobj?.controle?.nomeParametro || "Parâmetro",
    cpk: cobj?.estatisticas?.cpk,
    statAlert: hasStatAlert(cobj),
    qtdFora: cobj.estatisticas?.foraDeEspecificacaoTotal ?? (cobj.registros || []).filter(r => r.foraDeEspecificacao === true).length,
    operacao: cobj?.controle?.operacao || "Geral",
  }))


  // Modal de detalhes do registro
  const [registroModal, setRegistroModal] = useState(null);
  const closeModal = () => setRegistroModal(null);

  return (
    <>
      <Row className="g-3">
        {/* Seletor de PLANO e KPIs do plano (apenas estatística) */}

        <Col xs={12}>
          <PlanoDashboardCard
            selectedPlanoId={selectedPlanoId}
            setSelectedPlanoId={setSelectedPlanoId}
            planoOptions={planoOptions}
            estatisticasPlano={estatisticasPlano}
            estatisticasFrequencia={estatisticasFrequenciaPlano}
          />
        </Col>

        {/* Lista lateral de controles */}
        <Col md={3}>
          <ControleDashboardCard
            nomePlanoSelecionado={nomePlanoSelecionado}
            controleOptions={controleOptions}
            selectedControleId={selectedControleId}
            setSelectedControleId={setSelectedControleId}
           
          />
        </Col>

        {/* Painel do controle */}
        <Col md={9}>
          {current ? (
            <>
              <EstatisticaControleCard
                nomePlanoSelecionado={nomePlanoSelecionado}
                current={current}
                hasStatAlert={hasStatAlert}
              />

              {/* Tabela de Registros (sem colunas de intervalo/pontualidade) */}
                <RegistroviewDashboardCard
                  registros={current?.registros || []}
                  controle={current?.controle || {}}
                  setRegistroModal={setRegistroModal}
                  setShowHist={setShowHist}
                />

              {/* Modal: histograma de valores */}
              <ControleHistogramModal
                show={showHist}
                onHide={() => setShowHist(false)}
                valores={(current.registros || []).map((r) =>
                  Number(String(r.valor).replace(",", "."))
                )}
                limites={
                  current.estatisticas?.bounds ||
                  current.controle?.especificacao?.limitesDeControle ||
                  []
                }
                estatisticas={current.estatisticas || {}}
                unidade={current.controle?.labelUnidade || ""}
                titulo={
                    <div className="d-flex flex-column">
                        <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                            {current.controle?.operacao || "Operação Geral"}
                        </small>
                        <div className="d-flex align-items-baseline gap-2">
                            <span>{current.controle?.nomeParametro || "Parâmetro"}</span>
                            <span className="text-muted fw-normal small">
                                (ID: {current.controleId})
                            </span>
                        </div>
                    </div>
                }
              />
            </>
          ) : (
            <Card>
              <Card.Body>Selecione um controle na lista ao lado.</Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Modal de detalhes do registro (sem campos de intervalo) */}
      <Modal show={!!registroModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Detalhes do Registro</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {registroModal ? (
            <Table borderless size="sm" className="mb-0">
              <tbody>
                <tr>
                  <th style={{ width: 160 }}>ID</th>
                  <td>{registroModal.id}</td>
                </tr>
                <tr>
                  <th>Data</th>
                  <td>
                    {dayjs(registroModal.data).format(
                      "DD/MM/YYYY HH:mm:ss"
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Valor</th>
                  <td>
                    {fmtNum(registroModal.valor)}{" "}
                    {registroModal.unidade ? ` ${registroModal.unidade}` : ""}
                  </td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>
                    <Badge
                      bg={
                        registroModal.foraDeEspecificacao ? "danger" : "success"
                      }
                    >
                      {registroModal.foraDeEspecificacao
                        ? "Fora de especificação"
                        : "OK"}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </Table>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default  ProcessControleDashboard